import { DUMMY, cnl } from "csi/typings";
import { numberGetIntermediateSortValue } from "csi/utility/numberUtility";
import xml from "csi/xml";
import { green, red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ObservableMap, action, computed, makeObservable, observable } from "mobx";
import React, { MouseEventHandler } from "react";
import { BASE_COMPOSITION, BaseCompositionStore, createCompositionPlacementKey } from "./BaseCompositionStore";
import { CompositionPlacement, PlacementDetails } from "./CompositionPlacement";
import { isNullOrNaN } from "csi/utility/coreUtility";


interface CompareObject {
	compositionPlacements: PlacementDetails[];
	combineSameCompositionMaterials: boolean;
	includeCompositionPlacementNames: boolean;
	includeCompositionTypes: boolean;
}

export class SalesRegionException {
	readonly baseCompositionStore: BaseCompositionStore;

	/** Sales Region Exception identifier */
	@observable key: string;

	/** Sales Region Exception name */
	@observable name: string;

	/** current url of the exception */
	@observable currentUrl: cnl;

	/** sustainability flag */
	@observable hasSustainability: boolean;

	/** determines if the settings are showing */
	@observable showSettings: boolean = true;

	/** determines if same compositionMaterials should be combined on the righthandside */
	@observable combineSameCompositionMaterials: boolean = false;

	/** composition placement map */
	compositionPlacementMap: ObservableMap<string, CompositionPlacement> = observable.map<string, CompositionPlacement>(null, { deep: false });

	/** determines if placement names should be included on the righthand side */
	@observable includeCompositionPlacementNames: boolean = true;

	/** determines if compositiontypes should be included on the righthand side */
	@observable includeCompositionTypes: boolean = true;

	/** all the compositions */
	@computed get compositionPlacements(): CompositionPlacement[] {
		const compositionPlacements: CompositionPlacement[] = Array.from(this.compositionPlacementMap.values());

		// Sort the CompositionPlacement objects
		compositionPlacements.sort((a: CompositionPlacement, b: CompositionPlacement) => a.sortValue - b.sortValue);

		return compositionPlacements;
	}

	/** fetching nestUnderContainsPlacement */
	@computed get allNestUnderContainsPlacements(): string[] {
		const baseCompositionPlacements = new Set<string>();
		this.compositionPlacements?.forEach((element) => {
			element.getSortedBaseCompositionMaterials(this).forEach((value) => {
				const { nestUnderContainsPlacement, currentMaterialName } = value;
				if (nestUnderContainsPlacement && currentMaterialName) {
					baseCompositionPlacements.add(currentMaterialName);
				}
			});
		});
		return Array.from(baseCompositionPlacements);
	}

	/** check if the dialog contains a zero percent entry */
	get containsZeroPercent(): boolean {
		if (this.baseCompositionStore.fromStyleBom) {
			return this.compositionPlacements.some((placement) => {
				return placement.materialBOMs.some((material) => {
					return material.percentage === 0;
				});
			});
		}
		return this.compositionPlacements.some((placement) => {
			return placement.baseCompositionMaterials.some((composition) => {
				return composition.percentage === 0;
			});
		});
	}

	/** return true if every composition has a percentage */
	get compositionPlacementsHaveUnpopulatedPercentages(): boolean {
		const { compositionPlacements, hasSustainability } = this;
		if (hasSustainability) {
			return compositionPlacements.some(({ baseCompositionMaterials }) => baseCompositionMaterials.some(({ percentage }) => !percentage || percentage === 0));
		} else {
			return compositionPlacements.some(({ percentage }) => !percentage || percentage === 0);
		}
	}

	/** checks if there is more than one placement */
	get hasMultiplePlacements(): boolean {
		return this.compositionPlacements.length > 1;
	}

	/** Retrieves an array of all available ComponentItems for placement, including the DUMMY item */
	get allPlacementItemsAvailable(): ComponentItem[] {
		return [
			...this.baseCompositionStore.dummyItemList,
			...this.baseCompositionStore.placementNodes
				.map((node) => ({
					id: xml.nodeGetURL(node),
					invalid: !node.Active,
					node,
				}))];
	}

	/** returns true if input should auto focus */
	get shouldInputAutoFocus(): boolean {
		return this.baseCompositionStore.focusDropDownBoolean;
	}

	/** Retrieves an array of ComponentItems suitable for use in a dropdown menu */
	getPlacementItemsForDropdown = (index: number = -1): ComponentItem[] => {
		if (index < 0) {
			return this.allPlacementItemsAvailable.filter((item) => (!this.usedCompositionPlacementsWithoutDummy.has(item.id) && !item.invalid));
		}
		const { currentUrl } = this.compositionPlacements[index];
		return this.allPlacementItemsAvailable.filter((item) => (!this.usedCompositionPlacementsWithoutDummy.has(item.id) && !item.invalid) || item.id === currentUrl);
	};

	/** TODO: old check for positive, will need to be reworked */
	get compositionPercentagesAllPositive(): boolean {
		const { compositionPlacements, hasSustainability } = this;
		if (hasSustainability) {
			return compositionPlacements.every(({ baseCompositionMaterials }) => baseCompositionMaterials.every(({ percentage }) => percentage >= 0));
		} else {
			return compositionPlacements.every(({ percentage }) => percentage >= 0);
		}
	}

	/** display hint color based on disableSave */
	get hint(): { color: string } {
		const disableSave = false;
		return {
			color: disableSave ? red[600] : green[600],
		};
	};

	/** Checks the limit on each placement and returns true if each placement is at correct limit */
	get reachLimit(): boolean {
		const { compositionPlacements } = this;
		return compositionPlacements.every((placement) => placement.percentage === 100 || placement.percentage === 0);
	}

	/** If there is no BOM don't allow the user to create or select a placement */
	get disableNewPlacement(): boolean {
		return this.baseCompositionStore.fromStyleBom && !this.baseCompositionStore.currentSelectedBOM;
	}

	/** determines if the current salesRegionException is the baseComposition */
	get isBaseComposition(): boolean {
		return this.name === BASE_COMPOSITION;
	}

	/** Helper function to simplify checks across different conditions */
	private checkCompositionPlacements(predicate: (placement: CompositionPlacement) => boolean): boolean {
		return this.compositionPlacements.some(predicate);
	}

	/** Checks if any material or composition material contains a placeholder URL marked as 'DUMMY' */
	get bomMaterialOrCompositionMaterialContainsDUMMY(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.containsDUMMY;
		});
	}

	/** Checks if any BOM material lacks a composition */
	get noCompositionOnSomeBOMMaterial(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.hasBOMMaterialWithoutComposition;
		});
	}

	/** Checks if any composition material has zero percentage */
	get compositionMaterialHasZeroPercent(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.hasZeroPercentageCompositionMaterial;
		});
	}

	/** Checks if any composition material has null percentage */
	get compositionMaterialHasNullPercent(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.hasNullPercentageCompositionMaterial;
		});
	}

	/** Checks if any BOM material has zero or null percentage */
	get bomMaterialHasZeroPercent(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.hasZeroPercentageBOMMaterial;
		});
	}

	/** Checks if any BOM material has zero or null percentage */
	get bomMaterialHasNullPercent(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return placement.hasNullPercentageBOMMaterial;
		});
	}

	/** returns true if any placement does not have selected materials */
	get somePlacementNotHaveSelectedMaterials(): boolean {
		return this.checkCompositionPlacements((placement) => {
			return this.baseCompositionStore.fromStyleBom ? placement.materialBOMs.length === 0 : placement.baseCompositionMaterials.length === 0;
		});
	}

	/** Checks if not all placements have reached 100 percent */
	get somePlacementNot100Percent(): boolean {
		return !this.reachLimit;
	}

	/** Checks the limit on each placement and returns true if each placement is at correct limit */
	get everyPlacementIsZero(): boolean {
		const { compositionPlacements } = this;
		return compositionPlacements.every((placement) => placement.percentage === 0);
	}

	/** Checks if every placement contains at least one BOM material with a null or NaN percentage */
	get everyPlacementContainsNull(): boolean {
		const { fromStyleBom } = this.baseCompositionStore;
		return this.compositionPlacements.every((placement) => {
			return placement.percentage === 100 || 
					(fromStyleBom ? 
						placement.materialBOMs.some((material) => isNullOrNaN(material.rawPercentage)) 
						: placement.baseCompositionMaterials.some((material) => isNullOrNaN(material.percentage)));
		});
	}

	/** Retrieves the current CompositionPlacement instance associated with a given key. */
	getCurrentCompositionPlacement = (compositionPlacementKey: string): CompositionPlacement => {
		return this.compositionPlacements.find((placement) => {
			return placement.key === compositionPlacementKey;
		});
	};

	/** Gets the current dropdown value for a given CompositionPlacement. */
	getCurrentPlacementDropdownValue = (placement: CompositionPlacement): ComponentItem | null => {
		if (placement.currentUrl === DUMMY) {
			return null;
		}
		return this.allPlacementItemsAvailable.find((item) => item.id === placement.currentUrl) ?? null;
	};

	/** Retrieves a set of used CompositionPlacements, excluding DUMMY */
	get usedCompositionPlacementsWithoutDummy(): Set<cnl> {
		const usedPlacementSet = new Set(this.compositionPlacements
			.filter(({ currentUrl }) => currentUrl !== "DUMMY")
			.map(({ currentUrl }) => currentUrl));
		return usedPlacementSet;
	}

	get compoundStringCompositionLabel(): Record<number, string> {
		let compoundString = "";

		this.compositionPlacements.forEach((placement) => {
			if (this.includeCompositionPlacementNames) {
				compoundString += " " + placement.rightDrawerPlacementText + " ";
			}
			placement.getSortedBaseCompositionMaterials(this).forEach((sortedMaterial) => {
				compoundString += sortedMaterial.getRightDrawerMaterialText(this) + " ";
			});
		});

		const baseLanguage: Record<number, string> = { 0: compoundString };
		return baseLanguage;
	}

	@computed get compareObject(): CompareObject {
		return {
			compositionPlacements: this.compositionPlacements.map((placement) => placement.placementDetails),
			combineSameCompositionMaterials: this.combineSameCompositionMaterials,
			includeCompositionPlacementNames: this.includeCompositionPlacementNames,
			includeCompositionTypes: this.includeCompositionTypes,
		};
	}

	/** drop placement from one index to another */
	@action onDropPlacement = (currentPlacement: CompositionPlacement, monitor: any, initDropIndex: number, groupId: string): void => {
		const { compositionPlacements } = this;
		const dropIndex = compositionPlacements.length === initDropIndex ? compositionPlacements.length - 1 : initDropIndex;
		const destPlacement = compositionPlacements[dropIndex];
		// if dragIndex > dropIndex then place before, otherwise place after
		if (dropIndex === 0) {
			// if the drop index is 0 replace smallest sort value with current
			const tempSortValue = destPlacement.sortValue;
			destPlacement.setSortValue(numberGetIntermediateSortValue(
				destPlacement.sortValue,
				compositionPlacements[dropIndex + 1].sortValue,
			));
			currentPlacement.setSortValue(tempSortValue);
		} else if (initDropIndex === compositionPlacements.length) {
			// if the drop index is at the end, just append it.
			currentPlacement.setSortValue(destPlacement.sortValue + 1);
		} else {
			// otherwise place it in between the two
			currentPlacement.setSortValue(numberGetIntermediateSortValue(
				compositionPlacements[dropIndex - 1].sortValue,
				destPlacement.sortValue,
			));
		}
		this.baseCompositionStore.focusDropDownBoolean = false;
	};

	/** adds a new CompositionPlacement instance to a map and returns its generated key. */
	@action addCompositionPlacement = (url?: cnl): string => {
		const { compositionPlacementMap, compositionPlacements } = this;
		const { length } = compositionPlacements;
		const key = createCompositionPlacementKey();
		const sortValue = length < 1 ? 0 : compositionPlacements[length - 1].sortValue + 1;
		compositionPlacementMap.set(key, new CompositionPlacement(this.baseCompositionStore, key, sortValue, this.getPlacementItemsForDropdown(), url));
		this.baseCompositionStore.focusDropDownBoolean = true;
		return key;
	};

	/** adds a placement directly for ease of use while copying */
	@action addDeepCopyPlacement = (key: string, placement: CompositionPlacement): void => {
		this.compositionPlacementMap.set(key, placement);
		this.baseCompositionStore.focusDropDownBoolean = false;
	};

	/** remove composition placement */
	@action removePlacementComposition = (key: string): void => {
		this.compositionPlacementMap.delete(key);
	};

	/** toggle showSettings */
	@action setShowSettings: MouseEventHandler<HTMLButtonElement> = (event): void => {
		this.showSettings = !this.showSettings;
	};

	/** toggle showSettings with a boolean value */
	@action setShowSettingsWithBoolean = (value: boolean): void => {
		this.showSettings = value;
	};

	/** toggle includeCompositionPlacementNames */
	@action setIncludeCompositionPlacementNames = (event: React.ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.includeCompositionPlacementNames = value;
	};

	/** toggle combineSameCompositionMaterials */
	@action setCombineSameCompositionMaterials = (event: React.ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.combineSameCompositionMaterials = value;
	};

	/** toggle includeCompositionTypes */
	@action setIncludeCompositionTypes = (event: React.ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.includeCompositionTypes = value;
	};

	@action clearCompositionPlacementMap = (): void => {
		this.compositionPlacementMap.clear();
	};

	constructor(
		baseCompositionStore: BaseCompositionStore,
		exceptionKey: string,
		name: string,
		currentUrl: cnl,
	) {
		makeObservable(this);
		this.baseCompositionStore = baseCompositionStore;
		this.key = exceptionKey;
		this.name = name;
		this.currentUrl = currentUrl;
	}
}

